/**
 * 「版本查询」子页：主版本列表（design model-management-revamp §10）。
 *
 * 上方筛选（关键字 / 大版本 / 版本类型 / 分类 / 创建类型 / 创建人 / 创建日期），数据来自 GET /model/release/tree，
 * 全部在前端过滤。下方主版本列表：版本号、大版本、版本类型、分类、创建时间、创建类型、创建人、基线版本、描述。
 * 行首小三角展开主版本内容：模型类别、模型版本、小模型数；有改动记录时标出相对基线的变化。
 * 每个模型版本再展开为小模型列表：模型名称 + tag（文件后缀）。
 * 支持 ?name=xxx：定位并展开该主版本（主版本创建成功后会这样跳过来）。
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';
import { Button, DatePicker, Input, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import {
  getModelCategories,
  getRelease,
  getReleaseChanges,
  listReleaseTree,
  type ModelCategories,
  type ModelReleaseDetail,
  type ModelReleaseRow,
  type ReleaseChange,
} from '@/apis/model';
import SubModelTable, { SubModelCount } from './SubModelTable';
import {
  CHANGE_OP_LABEL,
  RELEASE_KIND_LABEL,
  RELEASE_TRIGGER_LABEL,
  categoryLabel,
  ndpTypeLabel,
  filterReleases,
  releaseNameFromSearch,
  type ReleaseFilter,
} from '@/utils/modelCatalog';

const { Text } = Typography;

function errText(e: unknown): string {
  const any = e as { response?: { data?: { message?: string } }; message?: string };
  return any?.response?.data?.message ?? any?.message ?? String(e);
}

function fmtTime(iso: string | null | undefined): string {
  return iso ? iso.replace('T', ' ').slice(0, 16) : '—';
}

const VersionQuery: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const target = releaseNameFromSearch(location.search);

  const [cats, setCats] = useState<ModelCategories | null>(null);
  const [rows, setRows] = useState<ModelReleaseRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState(target ?? '');
  const [majorGroup, setMajorGroup] = useState<string | undefined>();
  const [kind, setKind] = useState<string | undefined>();
  const [category, setCategory] = useState<string | undefined>();
  const [trigger, setTrigger] = useState<string | undefined>();
  const [creator, setCreator] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [expanded, setExpanded] = useState<string[]>(target ? [target] : []);

  useEffect(() => {
    getModelCategories().then(setCats).catch((e) => message.error(`加载模型分类失败：${errText(e)}`));
    listReleaseTree()
      .then((tree) => {
        const all = (tree?.groups ?? []).flatMap((g) => g.releases);
        all.sort((a, b) => b.create_time.localeCompare(a.create_time));
        setRows(all);
      })
      .catch((e) => message.error(`加载主版本列表失败：${errText(e)}`))
      .finally(() => setLoading(false));
  }, []);

  const opts = useMemo(() => {
    const uniq = (xs: string[]) => Array.from(new Set(xs.filter(Boolean))).sort();
    return {
      groups: uniq(rows.map((r) => r.major_group)).map((v) => ({ label: v, value: v })),
      creators: uniq(rows.map((r) => r.created_by)).map((v) => ({ label: v, value: v })),
      triggers: uniq(rows.map((r) => r.trigger_type)).map((v) => ({ label: RELEASE_TRIGGER_LABEL[v] ?? v, value: v })),
      categories: uniq(rows.map((r) => r.category ?? '')).map((v) => ({
        label: v === 'mixed' ? '混合' : categoryLabel(cats, v), value: v,
      })),
    };
  }, [rows, cats]);

  // 筛选栏只改草稿，点「查询」（或在关键字框回车）才生效
  const [applied, setApplied] = useState<ReleaseFilter>({ q: target ?? '' });
  // 关键字框的回车 / 清空会带上最新值（此时 q 状态可能还没更新）
  const search = (kw: string = q) => setApplied({
    q: kw, majorGroup, kind, category, trigger, creator,
    dateFrom: range?.[0]?.format('YYYY-MM-DD'), dateTo: range?.[1]?.format('YYYY-MM-DD'),
  });
  const filtered = useMemo(() => filterReleases(rows, applied), [rows, applied]);

  const reset = () => {
    setQ('');
    setMajorGroup(undefined);
    setKind(undefined);
    setCategory(undefined);
    setTrigger(undefined);
    setCreator(undefined);
    setRange(null);
    setApplied({});
    if (target) {
      navigate('/model-management/releases', { replace: true });
    }
  };

  const columns: ColumnsType<ModelReleaseRow> = [
    {
      title: '版本号', dataIndex: 'name', width: 270, fixed: 'left',
      render: (v: string) => (
        <Text strong copyable={{ text: v }} style={{ fontFamily: 'ui-monospace, monospace', background: v === target ? '#fff7e6' : undefined }}>
          {v}
        </Text>
      ),
    },
    { title: '大版本', dataIndex: 'major_group', width: 200, ellipsis: true },
    {
      title: '版本类型', dataIndex: 'kind', width: 90,
      render: (v: string) => <Tag color={v === 'release' ? 'blue' : 'orange'}>{RELEASE_KIND_LABEL[v] ?? v}</Tag>,
    },
    {
      title: '分类', dataIndex: 'category', width: 90,
      render: (v: string | null) => (v === 'mixed' ? '混合' : categoryLabel(cats, v)),
    },
    {
      title: '创建时间', dataIndex: 'create_time', width: 150,
      sorter: (a, b) => a.create_time.localeCompare(b.create_time), defaultSortOrder: 'descend',
      render: (v: string) => <Text style={{ fontSize: 12 }}>{fmtTime(v)}</Text>,
    },
    { title: '创建类型', dataIndex: 'trigger_type', width: 120, render: (v: string) => RELEASE_TRIGGER_LABEL[v] ?? v },
    {
      title: '中台发版名称', dataIndex: 'ndp_release_name', width: 200, ellipsis: { showTitle: false },
      render: (v: string | null | undefined) => (v
        ? <Tooltip title={v} placement="topLeft">{v}</Tooltip>
        : <Text type="secondary">—</Text>),
    },
    { title: '创建人', dataIndex: 'created_by', width: 200, ellipsis: true },
    {
      title: '基线版本', dataIndex: 'based_on', width: 250,
      render: (v: string | null) => (v
        ? <Text style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{v}</Text>
        : <Text type="secondary">—</Text>),
    },
    {
      title: '描述', dataIndex: 'description', width: 260, ellipsis: { showTitle: false },
      render: (v: string | null) => (v ? <Tooltip title={v} placement="topLeft">{v}</Tooltip> : <Text type="secondary">—</Text>),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ background: '#fff', border: '1px solid #ebebeb', borderRadius: 10, padding: '14px 16px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <Input.Search
            allowClear placeholder="版本号 / 描述 / 基线版本" style={{ width: 260 }}
            value={q} onChange={(e) => setQ(e.target.value)} onSearch={(v, _e, info) => (info?.source === 'clear' ? setApplied((p) => ({ ...p, q: '' })) : search(v))}
          />
          <Select allowClear showSearch placeholder="大版本" style={{ width: 200 }} value={majorGroup} options={opts.groups} onChange={setMajorGroup} />
          <Select
            allowClear placeholder="版本类型" style={{ width: 110 }} value={kind} onChange={setKind}
            options={Object.entries(RELEASE_KIND_LABEL).map(([k, v]) => ({ label: v, value: k }))}
          />
          <Select allowClear placeholder="分类" style={{ width: 110 }} value={category} options={opts.categories} onChange={setCategory} />
          <Select allowClear placeholder="创建类型" style={{ width: 130 }} value={trigger} options={opts.triggers} onChange={setTrigger} />
          <Select allowClear showSearch placeholder="创建人" style={{ width: 210 }} value={creator} options={opts.creators} onChange={setCreator} />
          <DatePicker.RangePicker
            value={range} onChange={(v) => setRange(v as [Dayjs | null, Dayjs | null] | null)}
            placeholder={['创建开始时间', '创建结束时间']} style={{ width: 280 }}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={() => search()}>查询</Button>
          <Button onClick={reset}>重置</Button>
          <span style={{ marginLeft: 'auto', color: '#6b6f76', fontSize: 12 }}>共 {filtered.length} 个</span>
        </div>
      </div>

      <Table<ModelReleaseRow>
        rowKey="name" size="middle" loading={loading} dataSource={filtered} columns={columns}
        scroll={{ x: 1800 }} pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false }}
        expandable={{
          expandedRowKeys: expanded,
          onExpandedRowsChange: (keys) => setExpanded(keys as string[]),
          expandedRowRender: (r) => <ReleaseItems name={r.name} cats={cats} />,
        }}
      />
    </div>
  );
};

// ============================================================
// 展开：主版本内容（模型类别 / 模型版本 / 小模型）
// ============================================================

interface ItemRow {
  name: string;
  version: string;
  category?: string;
  /** 主版本里冻结的二级分类 */
  type?: string | null;
  files: string[];
  change?: 'replace' | 'add';
  from?: string | null;
}

const ReleaseItems: React.FC<{ name: string; cats: ModelCategories | null }> = ({ name, cats }) => {
  const [detail, setDetail] = useState<ModelReleaseDetail | null>(null);
  const [changes, setChanges] = useState<ReleaseChange[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // 改动：新主版本读清单里的记录，旧主版本由后端按基线现算
    Promise.all([getRelease(name), getReleaseChanges(name)])
      .then(([d, c]) => {
        if (!cancelled) {
          setDetail(d);
          setChanges(c);
        }
      })
      .catch((e) => message.error(`加载主版本 ${name} 失败：${errText(e)}`))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [name]);

  const removed = changes.filter((c) => c.op === 'remove');
  const data: ItemRow[] = useMemo(() => {
    const byName = new Map(changes.filter((c) => c.op !== 'remove').map((c) => [c.name, c]));
    return (detail?.manifest.items ?? []).map((it) => {
      const c = byName.get(it.name);
      return {
        name: it.name, version: it.version, category: it.category, type: it.type ?? null,
        files: Object.entries(it.artifacts ?? {}).map(([tag, ref]) => ref?.file_name || tag).sort(),
        change: c?.op as ItemRow['change'], from: c?.from,
      };
    });
  }, [detail, changes]);

  const columns: ColumnsType<ItemRow> = [
    {
      title: '模型类别', dataIndex: 'name', width: 280,
      render: (v: string, r) => (
        <Space size={6}>
          <Text strong style={{ fontFamily: 'ui-monospace, monospace' }}>{v}</Text>
          {r.category && <Text type="secondary" style={{ fontSize: 12 }}>{categoryLabel(cats, r.category)}</Text>}
        </Space>
      ),
    },
    {
      // 与中台页面上的模型类型名一致（按主版本里冻结的二级分类查模型分类配置的中文名）
      title: '中台模型类别', dataIndex: 'type', width: 160,
      render: (v: string | null) => ndpTypeLabel(cats, v) ?? <Text type="secondary">—</Text>,
    },
    {
      title: '模型版本', dataIndex: 'version', width: 300,
      render: (v: string, r) => (
        <Space size={6}>
          <Text style={{ fontFamily: 'ui-monospace, monospace' }}>{v}</Text>
          {r.change && (
            <Tooltip title={r.change === 'replace' ? `基线版本里是 ${r.from ?? '—'}` : '基线版本里没有'}>
              <Tag color={r.change === 'replace' ? 'blue' : 'green'}>{CHANGE_OP_LABEL[r.change]}</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    { title: '小模型', width: 120, render: (_, r) => <SubModelCount files={r.files} /> },
  ];

  return (
    <div style={{ margin: '4px 0' }}>
      {removed.length > 0 && (
        <div style={{ marginBottom: 8, fontSize: 12, color: '#6b6f76' }}>
          相对基线移除：{removed.map((c) => <Tag key={c.name} color="red">{c.name}（{c.from ?? '—'}）</Tag>)}
        </div>
      )}
      <Table<ItemRow>
        rowKey="name" size="small" loading={loading} dataSource={data} columns={columns} pagination={false}
        expandable={{
          rowExpandable: (r) => r.files.length > 0,
          expandedRowRender: (r) => <SubModelTable files={r.files} />,
        }}
      />
    </div>
  );
};

export default VersionQuery;
